import { track } from "@amplitude/analytics-browser";
import clsx from "clsx";
import { useHistory, useLocation } from "react-router-dom";
import Page from "src/components/Page";
import { ThemeProvider, useTranslation } from "src/design-system/core";
import { ButtonContainer } from "src/design-system/molecules/buttons";
import { PageElement } from "src/design-system/molecules/layout/PageElement";
import { Tab, Tabs } from "src/design-system/molecules/navigation";
import { PATH_DASHBOARD, PATH_HOME, PATH_WELCOME_PAGE } from "src/routesConstants";
import type { Tab as TabElement } from "src/types/tab";
import { convertCamelCaseToKebabCase } from "src/utils/caseConverter";
import { DASHBOARD_TRANSLATION_NAMESPACE, DASHBOARD_VIEW_TK } from "src/views-ds/dashboard/config";
import { CreateOfferButton } from "src/views-ds/offer/components";
import { CreateSponsoredAdButton } from "src/views-ds/sponsored-ad/components/CreateSponsoredAdButton";
import {
    WELCOME_PAGE_VIEW_TK,
    WELCOME_PAGE_VIEW_TRANSLATION_NAMESPACE,
} from "src/views-ds/welcome/config";
import { AMPLITUDE_WELCOME_PAGE_KEY } from "src/views-ds/welcome/welcome-view/constants";

const HomePageTabListEnum = {
    Dashboard: "dashboard",
    Welcome: "welcome",
};

export function HomePageLayout({ children }: { children: React.ReactNode }): JSX.Element {
    const { tDashboard, tWelcome, translateCommon } = useTranslation({
        dashboard: `${DASHBOARD_TRANSLATION_NAMESPACE}:${DASHBOARD_VIEW_TK}`,
        welcome: `${WELCOME_PAGE_VIEW_TRANSLATION_NAMESPACE}:${WELCOME_PAGE_VIEW_TK}`,
    });

    const history = useHistory();
    const location = useLocation();

    const getTabIdFromPath = (pathname: string): string => {
        const lastSegment = pathname.split("/").filter(Boolean).pop();
        return lastSegment === HomePageTabListEnum.Welcome
            ? HomePageTabListEnum.Welcome
            : HomePageTabListEnum.Dashboard;
    };
    const currentTab = getTabIdFromPath(location.pathname);

    const handleTabChange = (id: string) => {
        track("click-tab", {
            page: "homepage",
            tab: convertCamelCaseToKebabCase(id),
        });
        const pathMap = {
            [HomePageTabListEnum.Dashboard]: PATH_DASHBOARD,
            [HomePageTabListEnum.Welcome]: PATH_WELCOME_PAGE,
        };
        history.push(pathMap[id] ?? PATH_HOME);
    };

    const tabList: TabElement[] = [
        { id: HomePageTabListEnum.Dashboard, title: tDashboard("title") },
        {
            id: HomePageTabListEnum.Welcome,
            title: tWelcome("title"),
            betaAdornment: translateCommon("concepts.new"),
        },
    ];

    return (
        <ThemeProvider theme="light" noDiv>
            <Page variant="bare">
                {/* div wrapper to remove the gap */}
                <div>
                    <PageElement isTopElement>
                        <div
                            className={clsx("ds-o-flex ds-o-flex--spaced ds-o-flex--space-between")}
                        >
                            <Tabs currentValue={currentTab} onChange={handleTabChange}>
                                {tabList.map((tab) => (
                                    <Tab
                                        key={`tab-header-${tab.id}`}
                                        value={tab.id}
                                        label={tab.title}
                                        betaAdornment={tab.betaAdornment}
                                    />
                                ))}
                            </Tabs>

                            {currentTab === HomePageTabListEnum.Welcome && (
                                <ButtonContainer>
                                    <CreateOfferButton
                                        hideShortcutAction
                                        groupedMoreButtonProps={{ variant: "outlined" }}
                                        dropdownHorizontalTransform="right"
                                        page={AMPLITUDE_WELCOME_PAGE_KEY}
                                    />
                                    <CreateSponsoredAdButton
                                        hideShortcutAction
                                        groupedMoreButtonProps={{ variant: "outlined" }}
                                        page={AMPLITUDE_WELCOME_PAGE_KEY}
                                    />
                                </ButtonContainer>
                            )}
                        </div>
                    </PageElement>
                    {children}
                </div>
            </Page>
        </ThemeProvider>
    );
}
